import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import CheckBox from 'components/CheckBox'
import Tooltip from 'components/Tooltip'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaMobileAlt, FaWhatsapp } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Container,
  Form,
  Column,
  CheckRow,
  SubTitle,
  Label
} from '../../styles'

export default function EditNotifications () {
  const [loading, setLoading] = useState (true)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [notificationConfig, setNotificationConfig] = useState ({
    allowSchedulePushNotifications: false,
    allowScheduleWhatsappNotifications: false,
    allowScheduleNotifications: false,
    allowUpdateNotifications: false,
    allowCancelNotifications: false
  })
  const hasEditPermission = AuthService.hasPermission ('business_config.agenda', 'edit')
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data: notificationConfig } = await cutileiApi.get (
        `/businesses/${businessId}/notification_info`, requestConfig
      )

      const {
        allow_schedule_push_notifications,
        allow_schedule_whatsapp_notifications,
        allow_schedule_notifications,
        allow_update_notifications,
        allow_cancel_notifications
      } = notificationConfig
    
      setNotificationConfig ({
        allowSchedulePushNotifications: allow_schedule_push_notifications,
        allowScheduleWhatsappNotifications: allow_schedule_whatsapp_notifications,
        allowScheduleNotifications: allow_schedule_notifications,
        allowUpdateNotifications: allow_update_notifications,
        allowCancelNotifications: allow_cancel_notifications
      })
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  if (loading) return <Loading style={{marginTop: 30}}/>

  return (
    <Formik
      initialValues={notificationConfig}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitting (true)
        
          await cutileiApi.post (
            `/businesses/${businessId}/notification_info`, values, requestConfig
          )

          resetForm ({ values })
          setSubmitting (false)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {({
        values,
        dirty,
        isSubmitting,
        setFieldValue,
        handleSubmit
      }) => (
        <Container>
          <Form>
            <SubTitle>Meios de notificação</SubTitle>
            <Column style={{gap: 0}}>
              <CheckRow>
                <CheckBox
                  id='allow_schedule_push_notifications'
                  value={values.allowSchedulePushNotifications}
                  disabled={!hasEditPermission}
                  onValueChange={value => setFieldValue ('allowSchedulePushNotifications', value)}
                />
                <Label htmlFor='allow_schedule_push_notifications'>
                  Notificações push (celular)
                </Label>
                <FaMobileAlt size={18} color='#35A7FF ' style={{marginTop: 1, marginLeft: 3}}/>
              </CheckRow>
              <CheckRow>
                <CheckBox
                  id='allow_schedule_whatsapp_notifications'
                  value={values.allowScheduleWhatsappNotifications}
                  disabled={!hasEditPermission}
                  onValueChange={value => setFieldValue ('allowScheduleWhatsappNotifications', value)}
                />
                <Label htmlFor='allow_schedule_whatsapp_notifications'>
                  Mensagens no WhatsApp
                </Label>
                <FaWhatsapp size={18} color='#44CF6C' style={{marginTop: 1, marginLeft: 3}}/>
              </CheckRow>
            </Column>
            <SubTitle>Notificações por eventos</SubTitle>
            <CheckRow>
              <CheckBox
                id='allow_schedule_notifications'
                value={values.allowScheduleNotifications}
                disabled={!hasEditPermission}
                onValueChange={value => setFieldValue ('allowScheduleNotifications', value)}
              />
              <Label htmlFor='allow_schedule_notifications'>
                Desejo receber notificações quando agendamentos forem criados
              </Label>
              <Tooltip
                id='allow_schedule_notifications'
                labelStyle={{width: 320}}
                message={
                  'Sempre que um cliente marcar um agendamento pela Cutilei ou pelo seu link de agendamentos online, '
                  + 'você será notificado em seu celular'
                }
              />
            </CheckRow>
            <CheckRow>
              <CheckBox
                id='allow_update_notifications'
                value={values.allowUpdateNotifications}
                disabled={!hasEditPermission}
                onValueChange={value => setFieldValue ('allowUpdateNotifications', value)}
              />
              <Label htmlFor='allow_update_notifications'>
                Desejo receber notificações quando houver alteração de agendamentos
              </Label>
              <Tooltip
                id='allow_update_notifications'
                labelStyle={{width: 320}}
                message='Sempre que um cliente alterar um agendamento, você será notificado em seu celular'
              />
            </CheckRow>
            <CheckRow>
              <CheckBox
                id='allow_cancel_notifications'
                value={values.allowCancelNotifications}
                disabled={!hasEditPermission}
                onValueChange={value => setFieldValue ('allowCancelNotifications', value)}
              />
              <Label htmlFor='allow_cancel_notifications'>
                Desejo receber notificações quando houver cancelamento de agendamentos
              </Label>
              <Tooltip
                id='allow_cancel_notifications'
                labelStyle={{width: 320}}
                message='Sempre que um cliente cancelar um agendamento, você será notificado em seu celular'
              />
            </CheckRow>
            {dirty && (
              <SecondaryButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                style={{width: 160, marginTop: 15, alignSelf: 'center'}}
              />
            )}
            <AlertDialog
              visible={showOkDialog}
              title='Sucesso'
              message='Notificações atualizadas!'
              confirmText='Ok'
              onConfirm={toggleOkDialog}
              onClose={toggleOkDialog}
            />
          </Form>
        </Container>
      )}
    </Formik>
  )
}
